import { createProfileSchema, isValidMerchantEmail } from '@common/validators';
import { FormTextField } from '@components/Form';
import { toast } from '@components/Toast';
import env from '@config/env';
import { ROUTE_PATHS } from '@config/routes';
import useDisableBrowserBack from '@hooks/useDisableBrowserBack';
import CreateProfilePageSkeleton from '@loaders/CreateProfilePageSkeleton';
import OnboardingPageLayout from '@modules/merchant/authentication/components/OnboardingPageLayout';
import ProfileImageUpload from '@modules/merchant/authentication/components/ProfileImageUpload';
import VerifyCodeModal from '@modules/merchant/onboarding/components/VerifyCodeModal';
import { getProfileImageValidationError } from '@modules/merchant/profile/utils/validateProfileImage';
import { completeOnboarding, setUser } from '@redux/auth/authSlice';
import { saveCreateProfile } from '@redux/onboarding/onboardingSlice';
import { fileApi, merchantAuthApi } from '@services/api';
import { getApiErrorMessage } from '@utils/apiError';
import { Formik, Form } from 'formik';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

const EMAIL_DELIVERY_CHANNEL = 'EMAIL';

const normalizeEmail = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const pickFirst = (...values) => values.find((value) => value != null && value !== '');

const extractProfilePayload = (response) => {
  const root = response?.data ?? response ?? {};
  const nested = root?.data && typeof root.data === 'object' ? root.data : root;
  return nested && typeof nested === 'object' ? nested : {};
};

const mapApiToFormValues = (data = {}) => {
  const profilePicUrl = String(
    pickFirst(data.profilePicUrl, data.profileImageUrl, data.avatar, '') || '',
  ).trim();

  return {
    firstName: String(pickFirst(data.firstName, data.first_name, '') || '').trim(),
    lastName: String(pickFirst(data.lastName, data.last_name, '') || '').trim(),
    email: String(pickFirst(data.email, '') || '').trim(),
    profileImage: profilePicUrl || null,
    profileImagePreview: profilePicUrl,
    emailVerified: Boolean(data.emailVerify ?? data.emailVerified),
  };
};

const resolveUploadResult = (payload) => {
  const data = payload?.data || payload;
  const unSignedUrl = data?.unSignedUrl || data?.unsignedUrl || '';
  const previewUrl = data?.signedUrl || data?.url || data?.fileUrl || unSignedUrl;
  return { unSignedUrl, previewUrl };
};

const EmailVerificationSync = memo(({ email, verifiedEmail, isVerified, onReset }) => {
  useEffect(() => {
    if (!isVerified) return;

    const current = normalizeEmail(email);
    if (!current || current === normalizeEmail(verifiedEmail)) return;

    onReset();
  }, [email, verifiedEmail, isVerified, onReset]);

  return null;
});

EmailVerificationSync.displayName = 'EmailVerificationSync';

const CreateProfilePage = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const { profile, loading } = useSelector((state) => state.onboarding);
  const [formValues, setFormValues] = useState({
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: profile.email || '',
    profileImage: profile.profileImagePreview || null,
  });
  const [imagePreview, setImagePreview] = useState(profile.profileImagePreview || '');
  const [profileLoaded, setProfileLoaded] = useState(false);
  // Email returned by get-profile; VERIFY stays disabled until the user changes it.
  const [prefilledEmail, setPrefilledEmail] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const sendingEmailOtpRef = useRef(false);

  useDisableBrowserBack();

  useEffect(() => {
    document.title = `Create Profile | ${env.APP_NAME}`;
  }, []);

  // Always fetch profile on mount so returning users get prefilled fields.
  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setProfileLoaded(false);

      try {
        const response = await merchantAuthApi.getProfile();
        if (cancelled) return;

        const mapped = mapApiToFormValues(extractProfilePayload(response));
        setFormValues({
          firstName: mapped.firstName,
          lastName: mapped.lastName,
          email: mapped.email,
          profileImage: mapped.profileImage,
        });
        setImagePreview(mapped.profileImagePreview || '');
        setPrefilledEmail(mapped.email);
        // Profile GET with an email means it is already on the account — show VERIFIED, disabled.
        setVerifyEmail(mapped.email);
        setIsEmailVerified(Boolean(mapped.email));
      } catch {
        // Keep onboarding draft values; user can still fill the form.
        if (!cancelled) {
          const fallbackEmail = profile.email || '';
          setFormValues({
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            email: fallbackEmail,
            profileImage: profile.profileImagePreview || null,
          });
          setImagePreview(profile.profileImagePreview || '');
          setPrefilledEmail(fallbackEmail);
          setVerifyEmail('');
          setIsEmailVerified(false);
        }
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
    // Refetch whenever this screen mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      if (imagePreview?.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    },
    [imagePreview],
  );

  const handleImageChange = async (file, setFieldValue) => {
    if (!file) return;

    const validationError = getProfileImageValidationError(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }

    const localPreview = URL.createObjectURL(file);
    setImagePreview(localPreview);
    setIsUploadingImage(true);

    try {
      const response = await fileApi.upload(file);
      const { unSignedUrl, previewUrl } = resolveUploadResult(response.data);

      if (localPreview.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview);
      }

      if (!unSignedUrl) {
        throw new Error('Upload response missing unSignedUrl');
      }

      // Store unSignedUrl for register API; use signed/preview URL for display.
      setImagePreview(previewUrl || unSignedUrl);
      setFieldValue('profileImage', unSignedUrl);
    } catch (error) {
      if (localPreview.startsWith('blob:')) {
        URL.revokeObjectURL(localPreview);
      }
      setImagePreview('');
      setFieldValue('profileImage', null);
      toast.error(getApiErrorMessage(error, 'Failed to upload image'));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleVerifyEmail = useCallback(
    async (email) => {
      const trimmedEmail = email?.trim();

      if (!trimmedEmail) {
        toast.error('Please enter your email address');
        return;
      }

      if (!isValidMerchantEmail(trimmedEmail)) {
        toast.error('Enter a valid email address');
        return;
      }

      const alreadyVerified =
        isEmailVerified && normalizeEmail(trimmedEmail) === normalizeEmail(verifyEmail);
      if (alreadyVerified || sendingEmailOtpRef.current) {
        return;
      }

      sendingEmailOtpRef.current = true;
      setIsSendingEmailOtp(true);
      try {
        await merchantAuthApi.sendContactOtp({
          deliveryChannel: EMAIL_DELIVERY_CHANNEL,
          email: trimmedEmail,
        });
        setVerifyEmail(trimmedEmail);
        setIsEmailVerified(false);
        setIsVerifyModalOpen(true);
      } catch {
        // Error toast is handled by the axios interceptor.
      } finally {
        sendingEmailOtpRef.current = false;
        setIsSendingEmailOtp(false);
      }
    },
    [isEmailVerified, verifyEmail],
  );

  const handleCloseVerifyModal = useCallback(() => {
    setIsVerifyModalOpen(false);
  }, []);

  const handleVerifyCode = useCallback(
    async (otp) => {
      // eslint-disable-next-line no-useless-catch
      try {
        await merchantAuthApi.verifyContactOtp({
          deliveryChannel: EMAIL_DELIVERY_CHANNEL,
          email: verifyEmail,
          otp,
        });
        setIsEmailVerified(true);
        toast.success('Email verified successfully');
      } catch (error) {
        // Error toast is handled by the axios interceptor.
        throw error;
      }
    },
    [verifyEmail],
  );

  const handleResetEmailVerification = useCallback(() => {
    setIsEmailVerified(false);
  }, []);

  const handleSubmit = async (values) => {
    try {
      const result = await dispatch(saveCreateProfile(values)).unwrap();
      const data = result?.data || result;
      const isNewUser = Boolean(data?.isNewUser ?? data?.user?.isNewUser);
      dispatch(
        setUser({
          ...currentUser,
          ...data?.user,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          profilePicUrl:
            imagePreview || (typeof values.profileImage === 'string' ? values.profileImage : ''),
          name: `${values.firstName} ${values.lastName}`.trim(),
          isNewUser,
        }),
      );
      toast.success(result?.message || 'Profile saved');
      dispatch(completeOnboarding());
      navigate(ROUTE_PATHS.SETUP_GUIDE);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to save profile');
    }
  };

  if (!profileLoaded) {
    return <CreateProfilePageSkeleton />;
  }

  return (
    <OnboardingPageLayout>
      <div className="flex w-full flex-col gap-0.75 desktop:gap-1">
        <h1 className="desktop:text-[32px] text-[24px] font-semibold desktop:leading-10 leading-8text-merchant-text-primary">
          Let&apos;s get you started
        </h1>
        <p className="desktop:text-[16px] text-sm font-medium leading-4.5 desktop:leading-6 text-merchant-text-secondary">
          Please enter your information
        </p>
      </div>

      <Formik
        initialValues={formValues}
        enableReinitialize
        validationSchema={createProfileSchema}
        validateOnChange
        onSubmit={handleSubmit}
      >
        {({ isValid, isSubmitting, setFieldValue, values }) => {
          const currentEmail = String(values.email || '').trim();
          const normalizedCurrent = normalizeEmail(currentEmail);
          const normalizedPrefill = normalizeEmail(prefilledEmail);
          const emailMatchesPrefill =
            Boolean(normalizedPrefill) && normalizedCurrent === normalizedPrefill;
          const isCurrentEmailVerified =
            isEmailVerified && normalizedCurrent === normalizeEmail(verifyEmail);
          const showVerified = isCurrentEmailVerified || emailMatchesPrefill;
          const isVerifyDisabled =
            !isValidMerchantEmail(currentEmail) || isSendingEmailOtp || showVerified;

          return (
            <Form className="flex w-full flex-1 flex-col gap-field">
              <EmailVerificationSync
                email={values.email}
                verifiedEmail={verifyEmail}
                isVerified={isEmailVerified}
                onReset={handleResetEmailVerification}
              />

              <ProfileImageUpload
                preview={imagePreview}
                uploading={isUploadingImage}
                onChange={(file) => handleImageChange(file, setFieldValue)}
              />

              <FormTextField name="firstName" label="First Name" placeholder="John" />
              <FormTextField name="lastName" label="Last Name" placeholder="Doe" />
              <FormTextField
                name="email"
                label="Email"
                placeholder="John.doe@gmail.com"
                type="email"
                suffix={
                  <button
                    type="button"
                    onClick={() => handleVerifyEmail(values.email)}
                    disabled={isVerifyDisabled}
                    className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm font-semibold leading-3.75 desktop:leading-5 text-merchant-primary outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {showVerified ? 'VERIFIED' : isSendingEmailOtp ? 'SENDING...' : 'VERIFY'}
                  </button>
                }
              />

              <button
                type="submit"
                disabled={
                  !isValid ||
                  isSubmitting ||
                  loading ||
                  isUploadingImage ||
                  !(emailMatchesPrefill || isCurrentEmailVerified)
                }
                className="mt-auto w-full desktop:min-h-11 min-h-8 cursor-pointer rounded-full bg-merchant-action px-(--space-section) py-2.5 desktop:py-3 text-sm font-semibold leading-3.75 desktop:leading-5 text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[#c2c6cf] disabled:opacity-100 sm:mt-4"
              >
                {loading || isSubmitting ? 'Saving...' : 'Continue'}
              </button>
            </Form>
          );
        }}
      </Formik>

      <VerifyCodeModal
        open={isVerifyModalOpen}
        email={verifyEmail}
        onClose={handleCloseVerifyModal}
        onVerify={handleVerifyCode}
      />
    </OnboardingPageLayout>
  );
});

CreateProfilePage.displayName = 'CreateProfilePage';

export default CreateProfilePage;
